import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    CircularProgress,
    Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import { downloadWorkorderReport } from "../../services/servicesService";

const formatReportDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    const pad = (part) => String(part).padStart(2, "0");
    return `${pad(date.getMonth() + 1)}-${pad(date.getDate())}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const WorkorderReportsModal = ({ open, onClose, woNumber, reports = [], onView }) => {
    const [downloadingReportKey, setDownloadingReportKey] = useState(null);
    const [downloadError, setDownloadError] = useState("");

    const handleDownload = async (event, report, reportKey) => {
        event.preventDefault();
        if (!report?.id || downloadingReportKey) return;

        setDownloadingReportKey(reportKey);
        setDownloadError("");
        try {
            const blob = await downloadWorkorderReport(report.id);
            if (!(blob instanceof Blob) || !blob.size || blob.type === "application/json") {
                throw new Error("Unable to download report.");
            }
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = report.file_name || "work-order-report.pdf";
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
        } catch (error) {
            setDownloadError(error.message || "Unable to download report.");
        } finally {
            setDownloadingReportKey(null);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                {woNumber ? `Work Order: ${woNumber}` : "Work Orders"}
                <IconButton aria-label="Close" onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                {downloadError && <Alert severity="error" sx={{ mb: 1 }}>{downloadError}</Alert>}
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>File Name</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Modified Date</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {reports.map((report) => {
                            const reportKey = report.id || `${report.type}-${report.file_name}`;
                            const isDownloading = downloadingReportKey === reportKey;
                            return (
                                <TableRow key={reportKey}>
                                    <TableCell>{report.file_name || "-"}</TableCell>
                                    <TableCell>{report.display_name || (report.type === "service" ? "Tech Service Report" : report.type === "inspection" ? "Inspection Report" : report.type) || "-"}</TableCell>
                                    <TableCell>{formatReportDate(report.source_modified_at || report.uploaded_at)}</TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="View report">
                                            <IconButton onClick={() => onView(report)} color="primary" size="small" aria-label="View report">
                                                <VisibilityIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Download report">
                                            <IconButton
                                                onClick={(event) => handleDownload(event, report, reportKey)}
                                                color="primary"
                                                size="small"
                                                aria-label="Download report"
                                                disabled={Boolean(downloadingReportKey)}
                                            >
                                                {isDownloading ? <CircularProgress size={18} sx={{ color: "error.main" }} /> : <DownloadIcon />}
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                {!reports.length && <div>No reports available.</div>}
            </DialogContent>
        </Dialog>
    );
};

export default WorkorderReportsModal;
